import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import SampleType from '../../../types/sample-type/SampleType';
import SampleTypeWriteDTO from '../../../types/sample-type/SampleTypeWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root'
})
export class SampleTypeService {

  private RESOURCE: string = 'sample-type';

  constructor(private baseService: BaseService) {}

  /**
   * Fetches the sample types from the backend.
   *
   * @param search The search query to filter the sample types by.
   * @param page The page number to fetch.
   * @param size The number of elements to fetch per page.
   * @return The sample types.
   */
  public async getSampleTypes(search?: string, page: number = 0, size: number = 10): Promise<Page<SampleType>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    if (search) {
      params = params.set('search', search);
    }

      return await this.baseService.get<Page<SampleType>>(this.RESOURCE, params);
  }

  /**
   * Fetches multiple sample types from the backend.
   *
   * @param ids The IDs of the sample types to fetch.
   * @return The sample types.
   */
  public async getMultipleSampleTypes(ids: number[]): Promise<SampleType[]> {
    const params = new HttpParams().set('ids', ids.join(','));

      return await this.baseService.get<SampleType[]>(this.RESOURCE + '/multiple', params);
  }

  /**
   * Fetches all sample types from the backend.
   *
   * @return The sample types.
   */
  public async getAllSampleTypes(): Promise<SampleType[]> {
      return await this.baseService.get<SampleType[]>(this.RESOURCE + '/all');
  }

  /**
   * Fetches a sample type from the backend.
   *
   * @param id The ID of the sample type to fetch.
   * @return The sample type.
   */
  public async getSampleType(id: number): Promise<SampleType> {
      return await this.baseService.get<SampleType>(this.RESOURCE + '/' + id);
  }

  /**
   * Creates a sample type in the backend.
   *
   * @param sampleType The sample type to create.
   * @return The response from the request.
   */
  public async createSampleType(sampleType: SampleTypeWriteDTO): Promise<void> {
      return await this.baseService.post<void>(this.RESOURCE, sampleType);
  }

  /**
   * Updates a sample type in the backend.
   *
   * @param sampleType The sample type to update.
   * @return The response from the request.
   */
  public async updateSampleType(sampleType: SampleTypeWriteDTO): Promise<void> {
      return await this.baseService.put<void>(this.RESOURCE + '/' + sampleType.id, sampleType);
  }

  /**
   * Deletes a sample type from the backend.
   *
   * @param id The ID of the sample type to delete.
   * @return The response from the request.
   */
  public async deleteSampleType(id: number): Promise<void> {
      return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
  }
}
